import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { buildTheme } from "./index";

const STORAGE_KEY = "color-mode";

const ColorModeContext = createContext({
  mode: "light",
  resolvedMode: "light",
  setMode: () => {},
  toggleMode: () => {},
});

export const useColorMode = () => useContext(ColorModeContext);

const prefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const readStoredMode = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    // Private mode / blocked storage — fall through to the default.
  }
  // Light is the default. Dark is opt-in via the toggle, and "system" only
  // applies if a user explicitly selects it.
  return "light";
};

export function ColorModeProvider({ children }) {
  // `mode` is the user's choice (may be "system"); `systemDark` tracks the OS.
  const [mode, setModeState] = useState(readStoredMode);
  const [systemDark, setSystemDark] = useState(prefersDark);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolvedMode = mode === "system" ? (systemDark ? "dark" : "light") : mode;

  const setMode = useCallback((next) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persistence is a convenience; the session still works without it.
    }
  }, []);

  const toggleMode = useCallback(() => {
    setMode(resolvedMode === "dark" ? "light" : "dark");
  }, [resolvedMode, setMode]);

  const theme = useMemo(() => buildTheme(resolvedMode), [resolvedMode]);

  // Keep the browser UI (address bar, form controls) in step with the theme.
  useEffect(() => {
    document.documentElement.style.colorScheme = resolvedMode;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme.palette.nav.bg);
  }, [resolvedMode, theme]);

  const value = useMemo(
    () => ({ mode, resolvedMode, setMode, toggleMode }),
    [mode, resolvedMode, setMode, toggleMode]
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default ColorModeProvider;
